import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaTicketAlt,
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
  FaCreditCard,
  FaTag,
} from "react-icons/fa";
import CountryCitySelection from "./actcomp/CountryCitySelection";
import ActivitySelection from "./actcomp/ActivitySelection";
import OptionSelection from "./actcomp/OptionSelection";
import VisitorSelection from "./actcomp/VisitorSelection";
import PriceSummary from "./actcomp/PriceSummary";
import BookingComplete from "./actcomp/BookingComplete";
import { fetchCountries, fetchCities, fetchActivities, fetchActivityOptions, fetchVisitorTypes } from "./actcomp/api";

const API_BASE_URL = (import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000") + "/api/activity";

const ActivityBooking = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [visitorDetails, setVisitorDetails] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [visitorTypes, setVisitorTypes] = useState([]);

  useEffect(() => {
    fetchCountries(setCountries);
    fetchVisitorTypes(setVisitorTypes);
  }, []);

  const handleCountrySelect = async (country) => {
    setSelectedCountry(country);
    setSelectedCity(null);
    setSelectedActivity(null);
    setSelectedOption(null);
    setCities([]);
    setActivities([]);
    setActivityOptions([]);
    await fetchCities(country._id, setCities);
    // Don't change step - stay on step 1 to show cities
  };

  const handleCitySelect = async (city) => {
    setSelectedCity(city);
    setSelectedActivity(null);
    setSelectedOption(null);
    setActivities([]);
    setActivityOptions([]);
    await fetchActivities(city._id, setActivities);
    // Stay on step 1 to show activities in horizontal scroller
  };

  const handleActivitySelect = async (activity) => {
    setSelectedActivity(activity);
    setSelectedOption(null);
    await fetchActivityOptions(activity._id, setActivityOptions);
    // Move to step 2 (options) after selecting activity
    setCurrentStep(2);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    const initialVisitors = visitorTypes.map((vt) => ({
      type: vt.name,
      typeId: vt._id,
      count: 0,
      multiplier: vt.priceMultiplier,
      minAge: vt.minAge,
      maxAge: vt.maxAge,
    }));
    setVisitors(initialVisitors);
    // Move to step 3 (Visitors & Time) after selecting option
    setCurrentStep(3);
  };

  const handleVisitorChange = (typeId, count) => {
    const newCount = parseInt(count) || 0;
    setVisitors((prev) =>
      prev.map((v) =>
        v.typeId === typeId ? { ...v, count: newCount } : v
      )
    );
    
    setVisitorDetails((prev) => {
      const currentDetails = prev[typeId] || [];
      const newDetails = [];
      for (let i = 0; i < newCount; i++) {
        newDetails.push(currentDetails[i] || { name: "", phone: "", age: "" });
      }
      return { ...prev, [typeId]: newDetails };
    });
  };

  const incrementVisitor = (typeId) => {
    const currentCount = visitors.find((v) => v.typeId === typeId)?.count || 0;
    handleVisitorChange(typeId, currentCount + 1);
  };

  const decrementVisitor = (typeId) => {
    const currentCount = visitors.find((v) => v.typeId === typeId)?.count || 0;
    if (currentCount > 0) {
      handleVisitorChange(typeId, currentCount - 1);
    }
  };

  const handleVisitorDetailChange = (typeId, index, field, value) => {
    setVisitorDetails((prev) => {
      const typeDetails = prev[typeId] || [];
      const updatedDetails = [...typeDetails];
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      return { ...prev, [typeId]: updatedDetails };
    });
  };

  const calculateTotalPrice = async () => {
    if (!selectedOption) {
      alert("Please select an activity option");
      return;
    }

    const totalVisitors = visitors.reduce((sum, v) => sum + v.count, 0);
    if (totalVisitors === 0) {
      alert("Please select at least one visitor");
      return;
    }

    if (!selectedDate) {
      alert("Please select a date");
      return;
    }

    setLoading(true);
    const selectedDateTime = new Date(`${selectedDate}T${selectedTime}:00`);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/user/calculate-price`,
        {
          activityOptionId: selectedOption._id,
          visitors: visitors
            .filter((v) => v.count > 0)
            .map((v) => ({
              type: v.type,
              count: v.count,
            })),
          selectedDateTime: selectedDateTime.toISOString(),
        }
      );

      setPriceBreakdown(response.data.data);
      setCurrentStep(4);
    } catch (error) {
      console.error("Error calculating price:", error);
      alert("Error calculating price. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBookingComplete = async () => {
    try {
      const allDetailsFilled = priceBreakdown?.breakdown?.every((item) => {
        const visitorType = visitors.find(v => v.type === item.type);
        if (!visitorType || item.count === 0) return true;
        const typeDetails = visitorDetails[visitorType.typeId] || [];
        return typeDetails.length === item.count && 
               typeDetails.every(detail => detail.name && detail.phone && detail.age);
      });

      if (!allDetailsFilled) {
        alert("Please fill in all visitor details (name, phone, and age) before confirming.");
        return;
      }

      const visitorsWithDetails = visitors
        .filter((v) => v.count > 0)
        .map((v) => {
          const typeDetails = visitorDetails[v.typeId] || [];
          return {
            type: v.type,
            typeId: v.typeId,
            count: v.count,
            details: typeDetails.map(detail => ({
              name: detail.name,
              phone: detail.phone,
              age: parseInt(detail.age) || 0,
            })),
          };
        });

      const bookingData = {
        activityOptionId: selectedOption._id,
        visitors: visitorsWithDetails,
        selectedDateTime: new Date(`${selectedDate}T${selectedTime}:00`).toISOString(),
        totalPrice: priceBreakdown?.total || 0,
        currency: priceBreakdown?.currency || selectedCountry?.currency || "",
        customerName: "",
        customerEmail: "",
        customerPhone: "",
      };

      const response = await axios.post(`${API_BASE_URL}/user/bookings`, bookingData);

      if (response.data.success) {
        setBookingComplete(true);
      } else {
        throw new Error(response.data.error || "Booking failed");
      }
    } catch (error) {
      console.error("Error completing booking:", error);
      alert("Error completing booking. Please try again.");
    }
  };

  const resetBookingFlow = () => {
    setCurrentStep(1);
    setSelectedCountry(null);
    setSelectedCity(null);
    setSelectedActivity(null);
    setSelectedOption(null);
    setSelectedDate("");
    setSelectedTime("10:00");
    setVisitors([]);
    setVisitorDetails({});
    setPriceBreakdown(null);
    setBookingComplete(false);
    setSearchTerm("");
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-white">
      {/* Header */}
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Content */}
        <div className="mt-8">
          {bookingComplete ? (
            <BookingComplete 
              selectedActivity={selectedActivity}
              selectedOption={selectedOption}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              visitors={visitors}
              priceBreakdown={priceBreakdown}
              selectedCountry={selectedCountry}
              resetBookingFlow={resetBookingFlow}
            />
          ) : (
            <div className="bg-white rounded-2xl shadow-xl border overflow-hidden">
              {/* Step Header */}
              <div className="border-b border-gray-200 px-8 py-6 bg-gradient-to-r from-gray-50 to-blue-50/30">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {currentStep === 1 && (
                        <>
                          {selectedCity ? `Activities in ${selectedCity.name}` : selectedCountry ? `Cities in ${selectedCountry.name}` : "Choose Your Destination"}
                        </>
                      )}
                      {currentStep === 2 && "Choose Package"}
                      {currentStep === 3 && "Add Visitors & Time"}
                      {currentStep === 4 && "Review & Book"}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {currentStep === 1 && (
                        <>
                          {selectedActivity ? `Selected: ${selectedActivity.name}` : selectedCity ? `Discover amazing activities in ${selectedCity.name}` : selectedCountry ? `Choose a city in ${selectedCountry.name}` : "Start your adventure by selecting a country"}
                        </>
                      )}
                      {currentStep === 2 && "Select the perfect package for your group"}
                      {currentStep === 3 && "Add visitors and choose your preferred time"}
                      {currentStep === 4 && "Review your booking details and proceed to payment"}
                    </p>
                  </div>
                  
                  {currentStep > 1 && (
                    <button
                      onClick={goBack}
                      className="flex items-center text-blue-600 hover:text-blue-800 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                  )}
                </div>
              </div>

              {/* Step Content */}
              <div className="p-8">
                {currentStep === 1 && (
                  <CountryCitySelection
                    countries={countries}
                    cities={cities}
                    activities={activities}
                    selectedCountry={selectedCountry}
                    selectedCity={selectedCity}
                    selectedActivity={selectedActivity}
                    handleCountrySelect={handleCountrySelect}
                    handleCitySelect={handleCitySelect}
                    handleActivitySelect={handleActivitySelect}
                    API_BASE_URL={API_BASE_URL}
                  />
                )}
                
                {currentStep === 2 && (
                  <OptionSelection
                    activityOptions={activityOptions}
                    selectedOption={selectedOption}
                    handleOptionSelect={handleOptionSelect}
                    selectedCountry={selectedCountry}
                  />
                )}
                
                {currentStep === 3 && (
                  <>
                    {!selectedOption ? (
                      <div className="text-center py-12">
                        <div className="text-gray-400 mb-4">
                          <FaTag className="w-16 h-16 mx-auto opacity-50" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No package selected
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Please go back and select a package first
                        </p>
                        <button
                          onClick={() => setCurrentStep(2)}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Back to Packages
                        </button>
                      </div>
                    ) : (
                      <VisitorSelection
                        visitors={visitors}
                        incrementVisitor={incrementVisitor}
                        decrementVisitor={decrementVisitor}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        selectedTime={selectedTime}
                        setSelectedTime={setSelectedTime}
                        selectedOption={selectedOption}
                        calculateTotalPrice={calculateTotalPrice}
                        loading={loading}
                      />
                    )}
                  </>
                )}
                
                {currentStep === 4 && (
                  <PriceSummary
                    selectedCountry={selectedCountry}
                    selectedCity={selectedCity}
                    selectedActivity={selectedActivity}
                    selectedOption={selectedOption}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    priceBreakdown={priceBreakdown}
                    visitors={visitors}
                    visitorDetails={visitorDetails}
                    handleVisitorDetailChange={handleVisitorDetailChange}
                    handleBookingComplete={handleBookingComplete}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!bookingComplete && currentStep !== 4 && (
          <div className="mt-8 flex justify-between items-center">
            <button
              onClick={resetBookingFlow}
              className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium hover:bg-gray-100 rounded-lg transition-colors"
            >
              Start Over
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Step {currentStep} of 4
              </div>
              {currentStep === 2 && selectedOption && (
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Help Button */}
      <button className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-shadow z-40">
        <FaCreditCard className="text-xl" />
      </button>
    </div>
  );
};

export default ActivityBooking;